/* ==========================================================================
   COSYTAS — Color swatch behaviour
   Used by: snippets/cosytas-swatch.liquid
   Requires: assets/cosytas-swatch.css for base styles

   Two responsibilities:

   A) Card mode (<span class="cosytas-swatch" role="button"> inside <product-card>)
      - Preload target images on hover
      - On click (or Enter/Space): fade-swap primary image, replace secondary (hover)
        image, update card link URL, title, price and badges
      - Toggle active state + aria-pressed between swatches
      - Re-initialise newly added product-card elements (AJAX / infinite scroll)

   B) PDP mode (<a class="cosytas-swatch" data-replace-history>)
      - Intercept clicks and use window.location.replace() instead of normal
        navigation, so the back button skips the intermediate product variant.
      - Prefetch on hover is handled by the theme via the data-instant attribute.

   C) Touch mode (pointer: coarse)
      - While a finger rests on a card image the secondary image is shown; lifting the
        finger restores the primary image. A normal tap opens the product as usual.

   D) Secondary image readiness
      - Marks the card figure with has-secondary-ready once the lazy hover image is loaded,
        so the primary image is only hidden when the hover image can actually be shown.
      - Desktop (pointer: fine): visible cards preload the hover image (IntersectionObserver).
      - Touch (pointer: coarse): no preload. The hover image is only requested on the first
        touch of a card (class is-secondary-requested, see cosytas-swatch.css).
   ========================================================================== */

(function() {
  'use strict';

  /* Touch-Geraet? (Finger statt Maus). Wird in D) und C) verwendet. */
  var coarsePointer = window.matchMedia ? window.matchMedia('(pointer: coarse)') : null;
  function isCoarsePointer() {
    return !!(coarsePointer && coarsePointer.matches);
  }

  /* ----------------------------------------------------------------
     D) HOVER-BILD BEREIT (Desktop und Touch)
     Das Theme laedt das zweite Kartenbild lazy mit niedriger Prioritaet.
     Wird die Karte gehovert (oder getippt), bevor das Bild da ist, blendet
     das Theme das Hauptbild trotzdem aus -> Karte kurz weiss.
     Hier: Klasse has-secondary-ready auf .product-card__figure, sobald das
     zweite Bild geladen ist; das CSS blendet das Hauptbild erst dann aus.
     Sichtbare Karten laden das zweite Bild sofort vor (IntersectionObserver);
     beim Hover/Touch wird das Laden zusaetzlich auf hohe Prioritaet gesetzt.
     ---------------------------------------------------------------- */

  function markSecondaryReady(img) {
    var figure = img.closest('.product-card__figure');
    if (figure) figure.classList.add('has-secondary-ready');
  }

  /* Wie die fruehere Hover-App: zweites Bild fuer Karten im (und kurz vor dem) sichtbaren
     Bereich sofort laden, statt lazy mit niedriger Prioritaet. So reagiert der erste
     Hover bzw. die erste Beruehrung ohne Verzoegerung. */
  var secondaryObserver = ('IntersectionObserver' in window) ? new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (!entry.isIntersecting) return;
      var img = entry.target;
      secondaryObserver.unobserve(img);
      if (img.complete && img.naturalWidth > 0) return;
      img.loading = 'eager';
      img.fetchPriority = 'auto';
    });
  }, { rootMargin: '300px 0px' }) : null;

  function watchSecondary(img) {
    if (img.complete && img.naturalWidth > 0) {
      markSecondaryReady(img);
      return;
    }
    img.addEventListener('load', function() { markSecondaryReady(img); }, { once: true });
    /* A2: auf Touch-Geraeten nicht vorladen, erst bei der ersten Beruehrung (Abschnitt C) */
    if (secondaryObserver && !isCoarsePointer()) secondaryObserver.observe(img);
  }

  function initSecondaryImages(root) {
    (root || document).querySelectorAll('.product-card__image--secondary').forEach(watchSecondary);
  }

  /* Beim Betreten der Karte das zweite Bild sofort mit hoher Prioritaet laden */
  document.addEventListener('pointerenter', function(event) {
    var figure = event.target && event.target.closest ? event.target.closest('.product-card__figure') : null;
    if (!figure) return;
    var img = figure.querySelector('.product-card__image--secondary');
    if (!img || (img.complete && img.naturalWidth > 0)) return;
    img.loading = 'eager';
    img.fetchPriority = 'high';
  }, true);

  /* ----------------------------------------------------------------
     A) CARD MODE
     ---------------------------------------------------------------- */

  var initedCards = new WeakSet();

  function initCard(card) {
    if (initedCards.has(card)) return;

    var swatches = card.querySelectorAll('.cosytas-swatch');
    if (!swatches.length) return;

    initedCards.add(card);

    var mediaLink   = card.querySelector('.product-card__media');
    var primaryImg  = card.querySelector('.product-card__image--primary');
    var secondaryImg = card.querySelector('.product-card__image--secondary');
    var titleEl     = card.querySelector('.product-title');
    var priceList   = card.querySelector('.price-list');
    var figure      = card.querySelector('.product-card__figure');

    /* Keep the theme's price text class (e.g. "h6") so the size stays identical after a swap */
    var priceBaseClass = '';
    if (priceList) {
      var existingPrice = priceList.querySelector('sale-price');
      if (existingPrice && existingPrice.classList.contains('h6')) priceBaseClass = 'h6 ';
    }

    swatches.forEach(function(s) {

      /* Preload target images on hover / focus for snappier swap */
      var preload = function() {
        if (this.dataset.primarySrc)   { var i  = new Image(); i.src  = this.dataset.primarySrc; }
        if (this.dataset.secondarySrc) { var i2 = new Image(); i2.src = this.dataset.secondarySrc; }
      };
      s.addEventListener('mouseenter', preload);
      s.addEventListener('focus', preload);

      /* Keyboard: Enter / Space behave like a click */
      s.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          this.click();
        }
      });

      s.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var self = this;

        if (self.classList.contains('cosytas-swatch--active')) return;

        /* Primary image — fade swap.
           Nach dem Tausch Inline-Opacity und -Transition wieder ENTFERNEN statt auf 1 setzen:
           eine Inline-Opacity uebersteuert die Hover-Regel des Themes (Hauptbild ausblenden),
           beide Bilder laegen dann additiv (mix-blend-mode: plus-lighter) uebereinander
           -> uebersaettigtes Hover-Bild nach einem Farbwechsel. */
        if (primaryImg && self.dataset.primarySrc) {
          primaryImg.style.transition = 'opacity 0.3s ease';
          primaryImg.style.opacity = '0';
          setTimeout(function() {
            primaryImg.setAttribute('srcset', self.dataset.primarySrcset || '');
            primaryImg.setAttribute('src', self.dataset.primarySrc);
            primaryImg.style.removeProperty('opacity');
            setTimeout(function() { primaryImg.style.removeProperty('transition'); }, 300);
          }, 300);
        }

        /* Secondary (hover) image */
        if (secondaryImg) {
          if (self.dataset.secondarySrc) {
            if (figure) figure.classList.remove('has-secondary-ready');
            secondaryImg.setAttribute('srcset', self.dataset.secondarySrcset || '');
            secondaryImg.setAttribute('src', self.dataset.secondarySrc);
            secondaryImg.style.display = '';
            watchSecondary(secondaryImg);
          } else {
            secondaryImg.style.display = 'none';
          }
        }

        /* Touch: Druck-Zustand (zweites Bild sichtbar) beim Farbwechsel zuruecksetzen */
        if (figure) figure.classList.remove('is-pressing');

        /* Card link URL */
        if (mediaLink && self.dataset.url) {
          mediaLink.setAttribute('href', self.dataset.url);
        }

        /* Title */
        if (titleEl && self.dataset.title) {
          titleEl.textContent = self.dataset.title;
          if (self.dataset.url) titleEl.setAttribute('href', self.dataset.url);
        }

        /* Price — same markup/classes as snippets/price-list.liquid (context: card) */
        if (priceList && self.dataset.price) {
          var cp = self.dataset.comparePrice;
          if (cp) {
            priceList.innerHTML = '<sale-price class="' + priceBaseClass + 'text-on-sale">' + self.dataset.price + '</sale-price><compare-at-price class="' + priceBaseClass + 'text-subdued line-through">' + cp + '</compare-at-price>';
          } else {
            priceList.innerHTML = '<sale-price class="' + priceBaseClass + 'text-subdued">' + self.dataset.price + '</sale-price>';
          }
        }

        /* Badges (sold out / custom / discount) — replace with the target product's badges.
           data-badges is always present in card mode (may be empty = no badges). */
        if (figure && typeof self.dataset.badges !== 'undefined') {
          var oldBadges = figure.querySelector('badge-list');
          if (oldBadges) oldBadges.remove();
          if (self.dataset.badges) {
            figure.insertAdjacentHTML('afterbegin', self.dataset.badges);
          }
        }

        /* Active state — toggle via class + aria-pressed (CSS handles the visual) */
        swatches.forEach(function(sw) {
          sw.classList.remove('cosytas-swatch--active');
          sw.setAttribute('aria-pressed', 'false');
        });
        self.classList.add('cosytas-swatch--active');
        self.setAttribute('aria-pressed', 'true');
      });
    });
  }

  function initAll() {
    initSecondaryImages(document);
    document.querySelectorAll('product-card').forEach(initCard);
  }

  /* Initial run — support defer-loaded script both before and after DOMContentLoaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Watch for dynamically added product-card elements (AJAX filters, infinite scroll) */
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'PRODUCT-CARD') {
            initSecondaryImages(node);
            initCard(node);
          } else if (node.querySelectorAll) {
            initSecondaryImages(node);
            node.querySelectorAll('product-card').forEach(initCard);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------------
     B) PDP MODE
     Event delegation: catches clicks on any <a class="cosytas-swatch"
     data-replace-history> regardless of when it was added to the DOM.
     ---------------------------------------------------------------- */

  document.addEventListener('click', function(event) {
    /* Only intercept primary clicks without modifier keys.
       Cmd/Ctrl/Shift/middle-click should still open in new tab as expected. */
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest('a.cosytas-swatch[data-replace-history]');
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    if (link.target === '_blank' || link.hasAttribute('download')) return;

    /* Already on this URL — block reload but don't navigate */
    if (link.href === window.location.href) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    window.location.replace(link.href);
  }, true /* capture: run before any other handler */);

  /* ----------------------------------------------------------------
     C) TOUCH MODE (pointer: coarse) — zweites Kartenbild bei Beruehrung
     Das Theme (Impact) zeigt das zweite Kartenbild nur bei Maus-Hover
     (@media pointer: fine). Auf Touch-Geraeten, wie die fruehere Hover-App:
       - Finger auf dem Kartenbild: zweites Bild sichtbar
       - Finger weg (touchend/touchcancel): zurueck zum ersten Bild
       - Ein normaler Tipp oeffnet die Produktseite wie gewohnt (kein preventDefault)
       - Umschaltung ohne Ueberblendung (CSS: transition none bei pointer: coarse)
     CSS dazu: .product-card__figure.is-pressing in cosytas-swatch.css
     ---------------------------------------------------------------- */

  /* coarsePointer / isCoarsePointer: siehe Anfang der Datei */

  function clearPressing() {
    document.querySelectorAll('.product-card__figure.is-pressing').forEach(function(f) {
      f.classList.remove('is-pressing');
    });
  }

  document.addEventListener('touchstart', function(event) {
    if (!isCoarsePointer()) return;
    var target = event.target;
    var media = target && target.closest ? target.closest('.product-card__media') : null;
    if (!media) return;
    var figure = media.closest('.product-card__figure');
    if (!figure) return;
    var secondary = figure.querySelector('.product-card__image--secondary');
    if (!secondary || secondary.style.display === 'none') return;

    /* A2: erst jetzt wird das zweite Bild eingeblendet (CSS) und damit geladen */
    figure.classList.add('is-secondary-requested');

    /* Zweites Bild sofort mit hoher Prioritaet laden, falls noch nicht da */
    if (!(secondary.complete && secondary.naturalWidth > 0)) {
      secondary.loading = 'eager';
      secondary.fetchPriority = 'high';
    }

    clearPressing();
    figure.classList.add('is-pressing');
  }, { capture: true, passive: true });

  document.addEventListener('touchend', clearPressing, { capture: true, passive: true });
  document.addEventListener('touchcancel', clearPressing, { capture: true, passive: true });

})();